import React, { useState, useEffect, useRef } from 'react';

const Joystick = ({ onMove, size = 100, stickSize = 50 }) => {
    const wrapperRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);

    const updatePosition = React.useCallback((clientX, clientY) => {
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const deltaX = clientX - centerX;
        const deltaY = clientY - centerY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        const maxDist = size / 2;

        let x = deltaX;
        let y = deltaY;

        if (distance > maxDist) {
            const angle = Math.atan2(deltaY, deltaX);
            x = Math.cos(angle) * maxDist;
            y = Math.sin(angle) * maxDist;
        }

        setPosition({ x, y });
        onMove({
            x: x / maxDist,
            y: y / maxDist
        });
    }, [size, onMove]);

    const handleStart = (clientX, clientY) => {
        setDragging(true);
        updatePosition(clientX, clientY);
    };

    const handleMove = React.useCallback((clientX, clientY) => {
        if (!dragging) return;
        updatePosition(clientX, clientY);
    }, [dragging, updatePosition]);

    const handleEnd = React.useCallback(() => {
        setDragging(false);
        setPosition({ x: 0, y: 0 });
        onMove({ x: 0, y: 0 });
    }, [onMove]);

    useEffect(() => {
        const handleMouseMove = (e) => handleMove(e.clientX, e.clientY);
        const handleMouseUp = () => handleEnd();
        const handleTouchMove = (e) => handleMove(e.touches[0].clientX, e.touches[0].clientY);
        const handleTouchEnd = () => handleEnd();

        if (dragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [dragging, handleMove, handleEnd]);

    return (
        <div
            ref={wrapperRef}
            className="joystick-base"
            style={{
                width: size,
                height: size,
            }}
            onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
            onTouchStart={(e) => handleStart(e.touches[0].clientX, e.touches[0].clientY)}
        >
            <div
                className="joystick-stick"
                style={{
                    width: stickSize,
                    height: stickSize,
                    transform: `translate(${position.x}px, ${position.y}px)`,
                }}
            />
        </div>
    );
};

export default Joystick;
